import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { GlobalService } from 'src/app/_services/global.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { SignupService } from 'src/app/_services/signup.service';
import { SubscriptionPlanService } from 'src/app/_services/subscription-plan.service';
import { PlanDataService } from 'src/app/_services/plan-data.service';
import { ComponentCanDeactivate } from 'src/app/_guards/unsaved-changes.guard';

/** Standalone "Continue to plans" page (LincSell Signup.dc.html prototype's plan-comparison
 *  screen), reached from the topbar Upgrade pill and from billing-and-plans's upgrade/downgrade
 *  confirm modal — previously an inline toggle inside billing-and-plans.component, now its own
 *  route so it has a real, linkable URL instead of only being reachable via component state. */
@Component({
  selector: 'app-change-plan',
  templateUrl: './change-plan.component.html',
  styleUrls: ['./change-plan.component.scss'],
})
export class ChangePlanComponent implements OnInit {
  _global = GlobalService;

  isMonthly = false;
  isLoading = true;
  plans: any[] = [];
  planData: any = null;
  currentPlanDetails: any = null;

  // Upgrade/downgrade execution dialog (ported from billing-and-plans.component) — needed here
  // because an existing paid subscriber picking a card must confirm + execute the plan change
  // without leaving this page.
  selectedPlanForSubscription = <any>{};
  ShowCustomConfirmation = false;
  CurrentAction = 0;
  ErrorMessage = '';
  currentSubscriptionCharges = 0;
  newSubscriptionCharges = 0;
  subscriptionChangeNotes = '';
  upgradeSucessMessage = '';
  isCancellingSubscription = false;

  constructor(
    public _signup: SignupService,
    private _router: Router,
    public _abd: AppBaseDataService,
    private _subscriptionPlanService: SubscriptionPlanService,
    private _planData: PlanDataService,
  ) { }

  // Mirrors PaymentPageComponent.CHECKOUT_LOCK_KEY — set once a checkout actually completes,
  // cleared only by a deliberate re-entry (billing-and-plans/topbar navigating here on purpose).
  // Landing here via browser Back after a completed purchase must not re-open plan picking.
  private static readonly CHECKOUT_LOCK_KEY = 'checkout-just-completed';

  ngOnInit(): void {
    let checkoutLocked = false;
    try { checkoutLocked = sessionStorage.getItem(ChangePlanComponent.CHECKOUT_LOCK_KEY) === 'true'; } catch { /* ignore */ }
    if (checkoutLocked) {
      this._router.navigate(['/app/billing-and-plans'], { replaceUrl: true });
      return;
    }
    this.currentPlanDetails = this._global.userInfo?.businessSetup?.[0] ?? null;
    this.loadCurrentPlan();
  }

  /** This page is a standalone top-level route (not nested under AppMainComponent's sidebar
   *  shell) so it can be reached from multiple contexts — some of which have no dashboard to
   *  return to (e.g. a not-yet-logged-in signup flow) — so the "Back to dashboard" link only
   *  renders when a session actually exists. */
  get isLoggedIn(): boolean {
    return !!this._global.getUserToken();
  }

  // Confirm-before-leaving when a checkout was already started from this page — mirrors
  // PaymentPageComponent's own "Leave checkout?" dialog, since a plan pick + any cart additions
  // (registers/locations/add-ons/hardware) made on /check-out are still sitting in localStorage
  // at this point and would otherwise be silently abandoned by "Back to dashboard".
  showLeaveWithCartConfirm = false;

  /** Whether /check-out actually has something in progress worth confirming about — a bare plan
   *  pick with nothing added yet isn't "a cart with items", so this checks the persisted cart's
   *  real content (same shape PaymentPageComponent.persistCartState() writes), not just whether a
   *  plan was ever picked. */
  // Same account-scoping as PaymentPageComponent.readPersistedCartState() — a cart stamped
  // with a different (or no) userID belongs to a previous account on this browser, not the
  // one currently signed in, and must not be treated as this account's cart.
  private currentUserId(): string | null {
    const id = this._global.userInfo?.userID ?? this._global.userInfo?.userId ?? this._global.userInfo?.UserID;
    return id != null ? String(id) : null;
  }

  private hasActiveCart(): boolean {
    try {
      const raw = localStorage.getItem('checkout-cart-state');
      if (!raw) return false;
      const cart = JSON.parse(raw);
      const ownerId = cart?.userId != null ? String(cart.userId) : null;
      const currentId = this.currentUserId();
      if (ownerId == null || currentId == null || ownerId !== currentId) return false;
      return !!(
        (Number(cart?.registerAddedQuantity) || 0) > 0 ||
        (Number(cart?.locationAddedQuantity) || 0) > 0 ||
        cart?.selectedAddonIds?.length ||
        cart?.selectedAddonNames?.length ||
        cart?.selectedHardware?.length
      );
    } catch {
      return false;
    }
  }

  private clearCheckoutState(): void {
    localStorage.removeItem('checkout-cart-state');
    localStorage.removeItem('selectedPlanDetails');
    this._signup.selectedPlanDetails = null;
  }

  goBack(): void {
    if (this.hasActiveCart()) {
      this.showLeaveWithCartConfirm = true;
      return;
    }
    this._router.navigate(['/app/billing-and-plans']);
  }

  confirmLeaveWithCart(leave: boolean): void {
    this.showLeaveWithCartConfirm = false;
    if (!leave) return;
    this.clearCheckoutState();
    this._router.navigate(['/app/billing-and-plans']);
  }

  private loadCurrentPlan(): void {
    this.isLoading = true;
    this._abd.showSpinner();
    this._subscriptionPlanService.getBusinessInvoice().subscribe(
      (res: any) => {
        if (res.responseStatusCode == 903) {
          this.planData = res.data.currentSubscription;
          this.isMonthly = this.planData?.subscriptionInterval === 1 ? true : false;
        }
        this.loadPlans();
      },
      () => {
        this._abd.hideSpinner();
        this.isLoading = false;
        this.loadPlans();
      }
    );
  }

  private loadPlans(): void {
    this._subscriptionPlanService.getSubscriptionPlanListV2().pipe(
      finalize(() => {
        this._abd.hideSpinner();
        this.isLoading = false;
      })
    ).subscribe((res) => {
      if (res.success) {
        const normalizedPlans = (res.data || []).map((plan: any) => ({
          ...plan,
          prices: this.buildPlanPrices(plan),
        }));
        this.plans = normalizedPlans
          .filter((p) => this.isSubscriptionPlan(p))
          .filter((p) => this.isPaidPlan(p))
          .filter((p) => !this.isAddOn(p))
          .map((item) => ({
            ...item,
            selectedPrice: this.extractPlanPrice(item),
          }));
      }
    });
  }

  currentInterval(value: boolean): void {
    this.isMonthly = value;
    this.isLoading = true;
    this.loadPlans();
    this._signup.selectedPlanInterval = value === true ? 'Monthly' : 'Yearly';
  }

  get billingCycleLabel(): string {
    if (this.isFreePlan) return 'Monthly';
    return this.planData?.subscriptionInterval === 12 ? 'Yearly' : 'Monthly';
  }

  get resolvedPlanName(): string {
    if (this.planData?.planName) return this.planData.planName;
    const setup = this._global.userInfo?.businessSetup?.[0];
    const planCosmosId = setup?.subscription?.planId ?? setup?.subscription?.PlanId;
    if (!planCosmosId) return 'Free';
    const matchedPlan = this._planData.list.find((p: any) => String(p.cosmosId) === String(planCosmosId));
    return matchedPlan?.planName || 'Free';
  }

  /** Days remaining in the account's free trial, for display on the current-plan card.
   *  Mirrors billing-and-plans.component.ts's daysLeft() trial branch. */
  get trialDaysLeft(): number | null {
    if (!this.currentPlanDetails?.isFreeTrial || !this.currentPlanDetails?.freeTrialEndDate) return null;
    const endDate = new Date(this.currentPlanDetails.freeTrialEndDate.toString());
    const diff = endDate.getTime() - new Date().getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 3600 * 24)));
  }

  /** businessSetup[0].subscription.planId is a cosmosId string (e.g. "1628516995007"), not the
   *  local catalog's small 1-4 id — resolveCurrentPlanId()'s `=== 1` check alone never actually
   *  matches a real account, which wrongly reported a genuine free-plan account as "paid" here and
   *  locked every card as unavailable. Match by cosmosId via the catalog first instead, same fix
   *  already applied in billing-and-plans.component.ts's isFreePlan / app.topbar.component.ts's
   *  isFreePlan. */
  get isFreePlan(): boolean {
    const planName = (this.planData?.planName || '').toString().trim().toLowerCase();
    if (planName === 'free') return true;
    const cosmosId = this.currentPlanCosmosId;
    if (cosmosId) {
      const matched = this._planData.list.find((p: any) => String(p.cosmosId) === cosmosId);
      if (matched) {
        return Number(matched.id) === 1 || String(matched.planName || '').trim().toLowerCase() === 'free';
      }
    }
    return this.resolveCurrentPlanId() === 1;
  }

  private resolveCurrentPlanId(): number | null {
    const rawId =
      this.planData?.planId ??
      this.planData?.planID ??
      this.currentPlanDetails?.subscription?.planId ??
      this.currentPlanDetails?.subscription?.planID ??
      this.currentPlanDetails?.subscription?.PlanId ??
      this.currentPlanDetails?.subscription?.PlanID ??
      null;
    const parsed = parseInt(rawId, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /** Same matching approach as resolvedPlanName/isFreePlan: businessSetup[0].subscription.planId
   *  is a cosmosId string, not the catalog's small numeric id, so "is this card the current plan"
   *  must compare cosmosId-to-cosmosId — comparing it against catalog `id` (as resolveCurrentPlanId
   *  does) never matches for any paid plan and left every non-Free card showing "Try free" even
   *  when it was the account's actual current plan. */
  private get currentPlanCosmosId(): string | null {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const cosmosId = setup?.subscription?.planId ?? setup?.subscription?.PlanId;
    return cosmosId != null ? String(cosmosId) : null;
  }

  /** Card existence, title and isActive all come from the live getSubscriptionPlanListV2()
   *  response (`this.plans`, already loaded/filtered to paid, non-add-on subscription plans in
   *  loadPlans()) rather than the local plan-list.json catalog — the two can drift (e.g. the API's
   *  "Plus" plan vs. the local file's "Standard", or a plan the API has retired). The local catalog
   *  is only consulted for frontend-only bits the API doesn't carry (a legacy numeric `id`), and as
   *  a `planPackages` fallback if a plan's live entry doesn't include its own feature list. Free
   *  never appears here because loadPlans() already filters to isPaidPlan() — matches the prior
   *  behavior, which explicitly excluded it from this same card list. */
  /** cosmosId of whatever plan is sitting in localStorage['selectedPlanDetails'] — set by
   *  goToCheckOut() when a card is picked, never cleared just by navigating back here (the
   *  Back pill on /check-out now returns straight to this page, see PaymentPageComponent.goBack()).
   *  Lets displayPlanCards flag that card as still "selected" rather than the picker looking like
   *  it forgot what the merchant already chose. */
  private get tempSelectedCosmosId(): string | null {
    try {
      const raw = localStorage.getItem('selectedPlanDetails');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const cosmosId = parsed?.cosmosId ?? parsed?.CosmosId;
      return cosmosId != null ? String(cosmosId) : null;
    } catch {
      return null;
    }
  }

  get displayPlanCards(): any[] {
    const currentCosmosId = this.currentPlanCosmosId;
    const tempSelectedCosmosId = this.tempSelectedCosmosId;
    const localByCosmosId = new Map<string, any>(
      (this._planData.list || []).map((p: any) => [String(p.cosmosId), p])
    );

    return this.plans
      .filter((lp: any) => (lp?.isActive ?? lp?.IsActive) !== false)
      .slice()
      .sort((a: any, b: any) => (Number(a.monthlyPrice) || 0) - (Number(b.monthlyPrice) || 0))
      .map((livePlan: any) => {
        const localMatch = localByCosmosId.get(String(livePlan.cosmosId));
        const title = livePlan.title ?? livePlan.Title ?? localMatch?.planName ?? '';
        const isCurrent = currentCosmosId !== null && String(livePlan.cosmosId) === currentCosmosId;
        // Once the account already has a paid plan, this comparison screen is view-only — no
        // switching plans from here (a paid plan is a one-time pick; only Support can change it
        // from that point on). Only a free/trial account can still pick a card to check out, so a
        // leftover localStorage selection from before the account went paid shouldn't be shown as
        // "Selected" anymore — that pick is no longer actionable.
        const isDisabled = !isCurrent && !this.isFreePlan;
        const isTempSelected = !isCurrent && !isDisabled && tempSelectedCosmosId !== null && String(livePlan.cosmosId) === tempSelectedCosmosId;
        const price = livePlan.selectedPrice?.value ?? (this.billingCycleLabel === 'Yearly' ? livePlan.yearlyPrice : livePlan.monthlyPrice) ?? 0;
        // Only trust the live planPackages if it's actually shaped the way the template expects
        // (title/icon/included) — this app's APIs are inconsistent about PascalCase vs. camelCase
        // per-field, so a live array in the wrong shape would otherwise silently win over the local
        // fallback and render a blank feature checklist instead of failing loudly.
        const livePackages = Array.isArray(livePlan.planPackages) && livePlan.planPackages.length
          ? livePlan.planPackages
          : null;
        const featuresLookCorrectlyShaped = livePackages?.every((f: any) => f?.title !== undefined && f?.included !== undefined);
        return {
          id: localMatch?.id ?? livePlan.cosmosId,
          cosmosId: livePlan.cosmosId,
          title,
          priceLabel: '$' + price,
          isFree: false,
          isCurrent,
          isTempSelected,
          isDisabled,
          badge: '1 month free trial · No credit card required',
          buttonLabel: isCurrent ? 'Your current plan' : (isDisabled ? 'Not available' : (isTempSelected ? 'Selected · Continue' : 'Get Started')),
          features: (featuresLookCorrectlyShaped ? livePackages : null) || localMatch?.planPackages || [],
          livePlan,
        };
      });
  }

  /** displayPlanCards is a getter that maps a fresh array of fresh objects on every call — without
   *  trackBy, Angular's default *ngFor identity check (by object reference) sees every change-
   *  detection pass as "all cards removed, all re-added" and destroys/recreates the DOM, including
   *  the CTA button. A mousedown on the button is itself enough to trigger that recreation, tearing
   *  down the exact button element before its paired click event fires — silently swallowing the
   *  first click. Same fix as billing-and-plans.component.ts's trackAddonById. */
  trackPlanCardById(_index: number, card: any): any {
    return card.cosmosId;
  }

  selectPlanCard(card: any): void {
    if (card.isCurrent || card.isDisabled) return;
    if (card.isFree) {
      this.performCancelSubscription();
      return;
    }
    if (card.livePlan) {
      this.goToCheckOut(card.livePlan);
    }
  }

  private performCancelSubscription(): void {
    if (this.isCancellingSubscription) return;
    this.isCancellingSubscription = true;
    this._abd.showSpinner();
    const isStripe =
      this._global.userInfo?.businessSetup?.[0]?.subscription?.transactionDetails?.paymentMethod === 'Stripe';
    this._subscriptionPlanService
      .cancelSubscriptionByCustomer(isStripe)
      .pipe(finalize(() => {
        this.isCancellingSubscription = false;
        this._abd.hideSpinner();
      }))
      .subscribe({
        next: (res: any) => {
          if (res?.success) {
            this.goBack();
          }
        },
      });
  }

  /** Every plan pick — trial or already-paying — now goes through the same /check-out page, which
   *  submits via saveUserSignupInfo() (FinishSignUpProcess), instead of an already-paying
   *  subscriber's pick going through the separate inline upgradeSubPlan()/downgradeSubPlan()
   *  confirm dialog (BusinessSubscription?operation=Update). Those two methods and the
   *  ShowCustomConfirmation dialog are unreachable from here now but left in place rather than
   *  removed, in case this unification needs reverting once verified against the live backend. */
  goToCheckOut(value: any): void {
    this._signup.selectedPlanDetails = value;
    localStorage.setItem('selectedPlanDetails', JSON.stringify(this._signup.selectedPlanDetails));
    this._router.navigate(['check-out']);
  }

  upgradePlan(): void {
    this.ErrorMessage = '';
    this.ShowCustomConfirmation = true;
    this.CurrentAction = 1;
    this.upgradeSubPlan('false');
  }

  downgradePlan(): void {
    this.ErrorMessage = '';
    this.ShowCustomConfirmation = true;
    this.CurrentAction = 2;
    this.downgradeSubPlan('false');
  }

  performAction(): void {
    this.ErrorMessage = '';
    if (this.CurrentAction == 1) {
      this.upgradeSubPlan();
    } else if (this.CurrentAction == 2) {
      this.downgradeSubPlan();
    }
  }

  closeConfirmationDialog(): void {
    this.ShowCustomConfirmation = false;
    if (this.upgradeSucessMessage.length > 0) {
      this.goBack();
    }
  }

  upgradeSubPlan(execute = 'true'): void {
    try {
      this.ErrorMessage = '';
      this.upgradeSucessMessage = '';
      this._abd.showSpinner();
      let newplan = { BuySubscriptionPlanId: this.selectedPlanForSubscription.planId };
      const stripe = this._global.userInfo.businessSetup[0]?.subscription?.transactionDetails?.paymentMethod === 'Stripe';
      this._subscriptionPlanService
        .upgradeSubscription(newplan, true, execute, stripe)
        .subscribe((res) => {
          if (res.success) {
            this.subscriptionChangeNotes = res.data.note;
            this.currentSubscriptionCharges = res.data.currentSubscriptionCharges;
            this.newSubscriptionCharges = res.data.newSubscriptionCharges;
            if (execute == 'true') {
              this.upgradeSucessMessage = 'You have sucessfully updated your plan.';
            }
          } else {
            this.ErrorMessage = 'Plan change failed, Please try again or contact support!';
          }
        });
    } catch { }
  }

  downgradeSubPlan(execute = 'true'): void {
    this.upgradeSucessMessage = '';
    try {
      this.ErrorMessage = '';
      this._abd.showSpinner();
      let newplan = { BuySubscriptionPlanId: this.selectedPlanForSubscription.planId };
      const stripe = this._global.userInfo.businessSetup[0]?.subscription?.transactionDetails?.paymentMethod === 'Stripe';
      this._subscriptionPlanService
        .upgradeSubscription(newplan, false, execute, stripe)
        .subscribe((res) => {
          if (res.success) {
            this.subscriptionChangeNotes = res.data.note;
            this.currentSubscriptionCharges = res.data.currentSubscriptionCharges;
            this.newSubscriptionCharges = res.data.newSubscriptionCharges;
            if (execute == 'true') {
              this.upgradeSucessMessage = 'You have sucessfully updated your plan.';
            }
          } else {
            this.ErrorMessage = 'An error occured during the operation, please try again.';
          }
        });
    } catch { }
  }

  private buildPlanPrices(plan: any): any[] {
    if (!plan) return [];
    if (Array.isArray(plan.prices) && plan.prices.length) return plan.prices;
    const prices: any[] = [];
    const monthly = Number(plan.monthlyPrice ?? plan.price);
    if (!isNaN(monthly) && monthly > 0) prices.push({ periodString: 'Monthly', value: monthly });
    const yearly = Number(plan.yearlyPrice);
    if (!isNaN(yearly) && yearly > 0) prices.push({ periodString: 'Yearly', value: yearly });
    return prices;
  }

  private isSubscriptionPlan(plan: any): boolean {
    return plan?.isSubscription === true || plan?.IsSubscription === true;
  }

  private isAddOn(plan: any): boolean {
    const type = (plan?.productType || '').toString().trim().toLowerCase();
    return (
      type === 'addon' ||
      type === 'add-on' ||
      type === 'add on' ||
      plan?.productType === 'AddOn' ||
      plan?.productType === 2 ||
      plan?.productType === 3
    );
  }

  private extractPlanPrice(plan: any) {
    if (!plan) return null;
    const prices = this.buildPlanPrices(plan);
    if (prices.length) {
      return prices.find((p: any) => p.periodString === (this.isMonthly ? 'Monthly' : 'Yearly')) || prices[0];
    }
    return null;
  }

  private getPlanPriceValue(plan: any): number {
    const price = this.extractPlanPrice(plan);
    return price?.value ?? 0;
  }

  private isPaidPlan(plan: any): boolean {
    if (!plan || plan.isTrial || plan.isCustom) return false;
    return this.getPlanPriceValue(plan) > 0;
  }
}
